// src/pages/Support/Support.jsx
import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Grid, CardActionArea, CircularProgress } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserFriends,
  faBullhorn
} from '@fortawesome/free-solid-svg-icons';
import { usePermissions } from '../../context/PermissionContext';
import BackButton from '../../components/BackButton';

// Define the Support options
const supportOptions = [
  {
    title: 'Q-Pay Help-Desk',
    icon: faUserFriends,
    path: '/support/customer-requests',
    perm: 'support.customer',
    description: 'Handle customer support tickets and inquiries'
  },
  {
    title: 'Ad Help-Desk',
    icon: faBullhorn,
    path: '/support/ad-support',
    perm: 'support.ad',
    description: 'Manage advertisement support issues'
  }
];

const Support = () => {
  const { can, loading } = usePermissions();
  const navigate = useNavigate();

  const handleCardClick = useCallback((path) => {
    navigate(path);
  }, [navigate]);

  if (loading) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '50vh' }}>
        <CircularProgress />
      </Grid>
    );
  }

  // Check if user has access to any of the cards
  const hasAnyAccess = supportOptions.some(option => can(option.perm));

  if (!hasAnyAccess) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '50vh' }}>
        <Typography variant="h6" color="error">
          You don't have permission to access support features. Please contact your administrator.
        </Typography>
      </Grid>
    );
  }

  return (
    <Box sx={{ position: 'relative', padding: 2 }}>
      <BackButton />
      <br/><br/>
      <Typography variant="h4" component="h1" gutterBottom sx={{ textAlign: 'center', mb: 4 ,mt:-7}}>
        Customer Help-Desk
      </Typography>
      
      <Grid container spacing={3} justifyContent="center" alignItems="center" sx={{ padding: 2 }}>
        {supportOptions.map((option) => (
          can(option.perm) && (
            <Grid item xs={12} sm={6} md={4} lg={3} key={option.title}>
              <Card
                elevation={3}
                sx={{
                  maxWidth: 225,
                  height: '100%',
                  backgroundColor: 'white',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    transform: 'translateY(-4px)',
                    boxShadow: '0 10px 20px rgba(0,0,0,0.2)',
                    '& .MuiTypography-root': { color: 'white' },
                    '& .fa-icon': { color: 'white' },
                  },
                }}
              >
                <CardActionArea 
                  onClick={() => handleCardClick(option.path)}
                  sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                >
                  <CardContent sx={{ textAlign: 'center', padding: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <FontAwesomeIcon
                      icon={option.icon}
                      size="5x"
                      className="fa-icon"
                      style={{ color: 'orange', marginBottom: '16px' }}
                    />
                    <Typography 
                      gutterBottom 
                      variant="h5" 
                      component="div" 
                      sx={{ fontWeight: 500 }}
                    >
                      {option.title}
                    </Typography>
                    <Typography 
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1, fontSize: '0.85rem' }}
                    >
                      {option.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          )
        ))}
      </Grid>
    </Box>
  );
};

export default Support;